import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Activity, Server, Globe, HardDrive, RefreshCw, Loader2,
  CheckCircle, XCircle, AlertTriangle, Play, Shield, Clock,
  ChevronDown, ChevronUp, Zap, Bell, BellOff, ToggleLeft, ToggleRight,
  Wrench
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAYBOOK_ICONS = {
  web_server: Globe,
  odoo_service: Server,
  disk_cleanup: HardDrive,
  performance: Activity,
};

const STATUS_STYLES = {
  healthy: { bg: "bg-green-500/10", border: "border-green-500/20", text: "text-green-400", icon: CheckCircle, label: "Healthy" },
  fixed: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400", icon: Wrench, label: "Auto-Fixed" },
  warning: { bg: "bg-yellow-500/10", border: "border-yellow-500/20", text: "text-yellow-400", icon: AlertTriangle, label: "Warning" },
  critical: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", icon: XCircle, label: "Critical" },
  unknown: { bg: "bg-zinc-500/10", border: "border-zinc-500/20", text: "text-zinc-400", icon: Clock, label: "Unknown" },
};

function PlaybookResult({ result, onRunPlaybook, running }) {
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_STYLES[result?.overall_status] || STATUS_STYLES.unknown;
  const StatusIcon = status.icon;
  const PbIcon = PLAYBOOK_ICONS[result?.playbook] || Activity;

  return (
    <div className={`border rounded-lg overflow-hidden ${status.border}`}
      data-testid={`playbook-result-${result?.playbook}`}>
      <button onClick={() => setExpanded(v => !v)}
        className={`w-full flex items-center gap-3 px-4 py-3 ${status.bg} text-left transition-all hover:opacity-90`}>
        <PbIcon className={`w-5 h-5 ${status.text} shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white">{result?.playbook_name || result?.playbook}</p>
          {result?.issues_found?.length > 0 && (
            <p className="text-xs text-zinc-400 mt-0.5">Issues: {result.issues_found.join(", ")}</p>
          )}
          {result?.fixes_applied?.length > 0 && (
            <p className="text-xs text-blue-400 mt-0.5">Fixes: {result.fixes_applied.join(", ")}</p>
          )}
        </div>
        <Badge className={`text-[10px] border ${status.bg} ${status.text} ${status.border}`}>
          <StatusIcon className="w-3 h-3 mr-1" /> {status.label}
        </Badge>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {expanded && result?.steps && (
        <div className="bg-black/40 p-3 space-y-1.5">
          {result.steps.map((step, i) => {
            const stepStyle = STATUS_STYLES[step.status] || STATUS_STYLES.unknown;
            return (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className={`w-4 h-4 shrink-0 mt-0.5 ${stepStyle.text}`}>
                  {step.status === "ok" ? <CheckCircle className="w-3.5 h-3.5" /> :
                   step.status === "warning" ? <AlertTriangle className="w-3.5 h-3.5" /> :
                   <XCircle className="w-3.5 h-3.5" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-300 font-medium">{step.name}</p>
                  <pre className="text-zinc-500 font-mono text-[10px] whitespace-pre-wrap mt-0.5 max-h-20 overflow-y-auto">{step.output}</pre>
                </div>
              </div>
            );
          })}
          {result?.fixes_applied?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/5">
              <p className="text-xs text-blue-400 font-medium mb-1">Auto-Fixes Applied:</p>
              {(typeof result.fixes_applied[0] === "string" ? result.fixes_applied.map(f => ({name: f})) : result.fixes_applied).map((fix, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Wrench className="w-3 h-3 text-blue-400 shrink-0" />
                  <span className="text-zinc-300">{fix.name}</span>
                  {fix.success !== undefined && (
                    <Badge className={`text-[9px] ml-auto ${fix.success ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                      {fix.success ? "OK" : "FAIL"}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationItem({ notif }) {
  const sev = STATUS_STYLES[notif.severity] || STATUS_STYLES.unknown;
  return (
    <div className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${sev.border} ${notif.read ? "opacity-60" : ""}`}>
      <Zap className={`w-4 h-4 mt-0.5 shrink-0 ${sev.text}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white">{notif.message}</p>
        <p className="text-[10px] text-zinc-500 mt-0.5">
          {notif.server_name} · {new Date(notif.timestamp).toLocaleString()}
        </p>
        {notif.fixes_applied?.length > 0 && (
          <p className="text-[10px] text-blue-400 mt-0.5">Applied: {notif.fixes_applied.join(", ")}</p>
        )}
      </div>
    </div>
  );
}

export default function OdooAutoPilot() {
  const { user } = useAuth();
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [playbooks, setPlaybooks] = useState([]);
  const [healthData, setHealthData] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState({});
  const [fullChecking, setFullChecking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autopilotEnabled, setAutopilotEnabled] = useState(true);
  const [activeView, setActiveView] = useState("dashboard");
  const [selectedResult, setSelectedResult] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [pbRes, healthRes, notifRes, statusRes] = await Promise.all([
        axios.get(`${API}/odoo/playbooks`),
        axios.get(`${API}/odoo/health`),
        axios.get(`${API}/odoo/notifications`),
        axios.get(`${API}/odoo/autopilot/status`),
      ]);
      setPlaybooks(pbRes.data);
      setHealthData(healthRes.data);
      setNotifications(notifRes.data);
      setAutopilotEnabled(statusRes.data.enabled);
      if (healthRes.data.length > 0 && !selectedServer) {
        setSelectedServer(healthRes.data[0].server_id);
      }
    } catch { toast.error("Failed to load AutoPilot data"); }
    finally { setLoading(false); }
  }, [selectedServer]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!selectedServer) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/odoo/history?server_id=${selectedServer}&limit=20`);
        setHistory(res.data);
      } catch {}
    })();
  }, [selectedServer]);

  const runPlaybook = async (playbookKey) => {
    if (!selectedServer) { toast.error("Select a server"); return; }
    setRunning(prev => ({ ...prev, [playbookKey]: true }));
    try {
      const res = await axios.post(`${API}/odoo/run-playbook?server_id=${selectedServer}&playbook=${playbookKey}&auto_fix=true`);
      setSelectedResult(res.data);
      toast.success(`${res.data.playbook_name}: ${res.data.overall_status}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Playbook failed");
    } finally {
      setRunning(prev => ({ ...prev, [playbookKey]: false }));
    }
  };

  const runFullCheck = async () => {
    if (!selectedServer) { toast.error("Select a server"); return; }
    setFullChecking(true);
    try {
      const res = await axios.post(`${API}/odoo/full-check?server_id=${selectedServer}`);
      setSelectedResult(res.data);
      toast.success(`Full check: ${res.data.overall}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Full check failed");
    } finally {
      setFullChecking(false);
    }
  };

  const toggleAutopilot = async () => {
    try {
      const res = await axios.post(`${API}/odoo/autopilot/toggle?enabled=${!autopilotEnabled}`);
      setAutopilotEnabled(res.data.enabled);
      toast.success(res.data.message);
    } catch { toast.error("Failed to toggle"); }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/odoo/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const currentHealth = healthData.find(h => h.server_id === selectedServer);
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="p-6 space-y-6" data-testid="odoo-autopilot-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight flex items-center gap-3">
            <Shield className="w-8 h-8 text-emerald-400" /> Odoo AutoPilot
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Automatic monitoring, diagnostics, and self-healing for Odoo servers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleAutopilot}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
              autopilotEnabled
                ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                : "bg-zinc-500/10 border-zinc-500/20 text-zinc-400"
            }`} data-testid="autopilot-toggle">
            {autopilotEnabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            AutoPilot {autopilotEnabled ? "ON" : "OFF"}
          </button>
          <Button variant="outline" onClick={fetchAll}
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
            data-testid="refresh-autopilot-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Server selector + health overview */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedServer} onValueChange={setSelectedServer}>
          <SelectTrigger className="w-72 bg-black/50 border-white/10 text-white text-sm h-9"
            data-testid="autopilot-server-select">
            <SelectValue placeholder="Select server..." />
          </SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-white/10">
            {healthData.length === 0 ? (
              <SelectItem value="none" disabled>No Linux servers with SSH</SelectItem>
            ) : (
              healthData.map(s => (
                <SelectItem key={s.server_id} value={s.server_id}>
                  <span className="text-sm">{s.server_name} ({s.ip_address})</span>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>

        {currentHealth && (
          <>
            {(() => {
              const h = STATUS_STYLES[currentHealth.odoo_health] || STATUS_STYLES.unknown;
              const HIcon = h.icon;
              return (
                <Badge className={`text-xs border ${h.bg} ${h.text} ${h.border}`}>
                  <HIcon className="w-3.5 h-3.5 mr-1" /> {h.label}
                </Badge>
              );
            })()}
            {currentHealth.last_health_check && (
              <span className="text-xs text-zinc-500">
                Last check: {new Date(currentHealth.last_health_check).toLocaleString()}
              </span>
            )}
          </>
        )}

        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
          onClick={runFullCheck} disabled={fullChecking || !selectedServer}
          data-testid="run-full-check-btn">
          {fullChecking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
          Run Full Check
        </Button>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 p-1 bg-black/30 border border-white/5 rounded-lg w-fit">
        {[
          { key: "dashboard", label: "Dashboard", icon: Activity },
          { key: "notifications", label: `Alerts ${unreadCount > 0 ? `(${unreadCount})` : ""}`, icon: Bell },
          { key: "history", label: "History", icon: Clock },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveView(tab.key)}
            className={`px-4 py-2 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
              activeView === tab.key ? "bg-emerald-500/20 text-emerald-400" : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`} data-testid={`tab-${tab.key}`}>
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Dashboard View */}
          {activeView === "dashboard" && (
            <div className="space-y-4">
              {/* Health cards for all servers */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {healthData.map(server => {
                  const h = STATUS_STYLES[server.odoo_health] || STATUS_STYLES.unknown;
                  const HIcon = h.icon;
                  const isSelected = server.server_id === selectedServer;
                  return (
                    <button key={server.server_id}
                      onClick={() => setSelectedServer(server.server_id)}
                      className={`p-4 rounded-lg border text-left transition-all ${h.border} ${h.bg} ${
                        isSelected ? "ring-1 ring-white/20" : "hover:ring-1 hover:ring-white/10"
                      }`} data-testid={`health-card-${server.server_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-white">{server.server_name}</span>
                        <Badge className={`text-[10px] border ${h.bg} ${h.text} ${h.border}`}>
                          <HIcon className="w-3 h-3 mr-1" /> {h.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-zinc-500 font-mono">{server.ip_address}</p>
                      {server.last_health_check && (
                        <p className="text-[10px] text-zinc-600 mt-1">
                          {new Date(server.last_health_check).toLocaleString()}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Playbook buttons */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {playbooks.map(pb => {
                  const PbIcon = PLAYBOOK_ICONS[pb.key] || Activity;
                  const isRunning = running[pb.key];
                  return (
                    <button key={pb.key}
                      onClick={() => runPlaybook(pb.key)}
                      disabled={isRunning || !selectedServer}
                      className="p-4 bg-[#0c0c0e] border border-white/5 hover:border-emerald-500/20 rounded-lg text-left transition-all group disabled:opacity-50"
                      data-testid={`playbook-btn-${pb.key}`}>
                      <PbIcon className="w-6 h-6 text-emerald-400 mb-2 group-hover:scale-110 transition-transform" />
                      <p className="text-sm font-medium text-white">{pb.name}</p>
                      <p className="text-[10px] text-zinc-500 mt-1">{pb.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[10px] text-zinc-600">{pb.steps_count} checks · {pb.fixes_count} fixes</span>
                        {isRunning && <Loader2 className="w-3 h-3 animate-spin text-emerald-400" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Latest result */}
              {selectedResult && (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-400 uppercase font-medium">Latest Run Result</p>
                  {selectedResult.reports ? (
                    Object.entries(selectedResult.reports).map(([key, report]) => (
                      <PlaybookResult key={key} result={report} />
                    ))
                  ) : (
                    <PlaybookResult result={selectedResult} />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notifications View */}
          {activeView === "notifications" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-400">{notifications.length} alerts</p>
                {unreadCount > 0 && (
                  <Button size="sm" variant="outline" onClick={markAllRead}
                    className="bg-white/5 border-white/10 text-white text-xs h-7">
                    <BellOff className="w-3 h-3 mr-1" /> Mark All Read
                  </Button>
                )}
              </div>
              {notifications.length === 0 ? (
                <Card className="bg-[#0c0c0e] border-white/5">
                  <CardContent className="py-12 text-center space-y-2">
                    <Bell className="w-10 h-10 text-zinc-700 mx-auto" />
                    <p className="text-sm text-white">No alerts yet</p>
                    <p className="text-xs text-zinc-500">AutoPilot alerts will appear here when issues are detected or fixed.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {notifications.map(n => <NotificationItem key={n.id} notif={n} />)}
                </div>
              )}
            </div>
          )}

          {/* History View */}
          {activeView === "history" && (
            <div className="space-y-2">
              <p className="text-sm text-zinc-400">{history.length} health checks</p>
              {history.length === 0 ? (
                <Card className="bg-[#0c0c0e] border-white/5">
                  <CardContent className="py-12 text-center space-y-2">
                    <Clock className="w-10 h-10 text-zinc-700 mx-auto" />
                    <p className="text-sm text-white">No history yet</p>
                    <p className="text-xs text-zinc-500">Run a health check or wait for AutoPilot to scan.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {history.map(h => {
                    const st = STATUS_STYLES[h.overall] || STATUS_STYLES.unknown;
                    const StIcon = st.icon;
                    return (
                      <div key={h.id} className={`flex items-center gap-3 p-3 bg-[#0c0c0e] border rounded-lg ${st.border}`}>
                        <StIcon className={`w-5 h-5 shrink-0 ${st.text}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">{h.server_name}</p>
                          <div className="flex gap-2 mt-0.5 flex-wrap">
                            {h.reports && Object.entries(h.reports).map(([k, v]) => {
                              const rs = STATUS_STYLES[v.overall_status] || STATUS_STYLES.unknown;
                              return (
                                <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded border ${rs.bg} ${rs.text} ${rs.border}`}>
                                  {k}: {rs.label}
                                  {v.fixes_applied?.length > 0 && ` (${v.fixes_applied.length} fixes)`}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        <span className="text-[10px] text-zinc-600 shrink-0">
                          {new Date(h.timestamp).toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
